import React from 'react'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {closeModal} from '../../redux/features/modal'
import {showToast} from '../../redux/features/toast'

import copyToClipboard from '../../../../commons/src/services/copy-to-clipboard'
import * as userApi from '../../services/api/user'
import {captureException} from '../../services/logging'

import keyIcon from '../../../../commons/src/assets/icons/key.svg'

import Button from '../../../../commons/src/components/button'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import InputField from '../../../../commons/src/components/input-field'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Modal from '../../../../commons/src/components/modal'
import Paragraph from '../../../../commons/src/components/paragraph'
import Section from '../../../../commons/src/components/section'
import Stack from '../../../../commons/src/components/stack'

type Props = {
  onError: (error: string) => void
  onSuccess: () => void
}

const ApiKeyModal = ({onError, onSuccess}: Props) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(state => state.auth.user)
  const [isLoading, setIsLoading] = React.useState(true)
  const [apiKey, setApiKey] = React.useState<string | null>(null)

  const generateApiKey = async () => {
    if (!user?.attributes.sub) {
      onError?.('User session not available.')
      dispatch(closeModal())
      return
    }

    try {
      setIsLoading(true)
      const response = await userApi.generateApiKey(user.attributes.sub)
      setApiKey(response.apiKey)
      onSuccess?.()
    } catch (err) {
      captureException(err)
      onError?.('Failed to generate API key. Try again later.')
      dispatch(closeModal())
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    generateApiKey()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isLoading && !apiKey) {
    return null
  }

  return (
    <Modal
      onClose={() => dispatch(closeModal())}
      buttons={
        <Button
          onClick={() => {
            copyToClipboard(apiKey ?? '')
            dispatch(showToast({content: 'API key copied to clipboard'}))
            dispatch(closeModal())
          }}
          disabled={isLoading}
        >
          Copy API Key and close
        </Button>
      }
    >
      <Section>
        {isLoading && (
          <div className="u-align-center">
            <Paragraph className="u-no-margin-bottom">Generating API Key...</Paragraph>
            <LoadingIndicator />
          </div>
        )}
        {!isLoading && apiKey && (
          <Stack>
            <Headline label="Your API Key" className="u-no-margin-bottom" />
            <Paragraph size="s" className="u-no-margin-bottom">
              This is your one-time API key. Make sure to copy it as you will not be able to see it
              again.
            </Paragraph>
            <InputField disabled icon={<Icon source={keyIcon} />} value={apiKey} />
          </Stack>
        )}
      </Section>
    </Modal>
  )
}

export default ApiKeyModal
